import React, { ReactElement, forwardRef } from 'react';
import { isNil } from 'lodash';
import classNames from 'classnames';
import { Container } from '@fiverr-private/layout_components';
import { mapResponsiveStyles } from '@fiverr-private/theme';
import { findChildByType, renderChildren } from '@fiverr-private/ui_utils';
import { getBorderStyling } from '../../shared/getTextInputsStyle';
import { filterProps } from '../../shared/utils';
import { getTextareaStylingProps } from '../style';
import { TEXTAREA_ID, TEXTAREA_SLOT_ID } from '../constants';
import { resizeClass } from '../styles.ve.css';
import { borderFocusWithinVar, container, resetStyling } from './styles.ve.css';
import type { TextareaGroupProps } from './types';
import { POSITION_MAPPER } from './constants';

const TextareaGroup = forwardRef<HTMLTextAreaElement, TextareaGroupProps>(
  (
    {
      children,
      disabled,
      isSuccess,
      isError,
      dataTestId,
      width = '100%',
      resize,
      onBlur,
      className,
      dataTrackTag = 'textarea_group',
      dataTrackValue,
      ...rest
    },
    ref
  ) => {
    const { focusBorderColor, borderColor, inlineVars } = getBorderStyling({
      disabled,
      isError,
      isSuccess,
      borderFocusWithinVar,
    });
    const { stylingProps, restProps } = filterProps(rest);
    const textareaPanel = findChildByType(children, TEXTAREA_SLOT_ID) as ReactElement;
    const textarea = findChildByType(children, TEXTAREA_ID) as ReactElement;
    const direction =
      !isNil(textareaPanel?.props.position) &&
      mapResponsiveStyles(textareaPanel?.props?.position, (value) => POSITION_MAPPER[value]);
    const childClassName = classNames(resetStyling, textarea?.props?.className);
    return (
      <Container
        dataTrackTag={dataTrackTag}
        dataTrackValue={dataTrackValue}
        overflow="hidden"
        data-testid={dataTestId}
        alignItems="flexEnd"
        direction={direction}
        padding="2"
        gap="2"
        width={width}
        onBlur={onBlur}
        style={inlineVars}
        className={classNames(container, className, {
          [resizeClass]: resize,
        })}
        {...getTextareaStylingProps({
          borderColor,
          disabled,
          focusBorderColor,
          focusType: 'focusWithin',
        })}
        {...stylingProps}
      >
        {renderChildren(textarea, {
          ref,
          disabled,
          className: childClassName,
          height: resize && !textarea?.props.height ? '100%' : textarea?.props.height,
          paddingX: '2',
          paddingY: '0',
          borderRadius: 'none',
          ...restProps,
        })}
        {textareaPanel}
      </Container>
    );
  }
);

TextareaGroup.displayName = 'TextareaGroup';

export default TextareaGroup;
